/**
 * Daily streak logic — PURE, dependency-free.
 *
 * Ported verbatim (same algorithm, same shapes) from the mobile app's
 * services/streak/dailyStreakLogic.ts so the web and mobile apps agree on
 * how a streak advances. One text translation per day keeps it alive, with
 * two repair mechanics so it isn't brittle:
 *
 *  - Shabbat rest: Saturday is a free day. It never requires activity and
 *    never breaks a streak. (A Saturday-only gap is bridged.)
 *  - Freezes: a small pool that auto-covers a single missed non-Saturday
 *    day, earned slowly as the streak grows.
 *
 * Kept pure so it can be unit-tested exhaustively. store.ts owns
 * localStorage persistence and calls into this.
 */

export interface DailyStreakState {
  currentStreak: number
  longestStreak: number
  /** Local calendar day of last activity, 'YYYY-MM-DD'. */
  lastActiveDate: string | null
  /** Local calendar day the current run started, 'YYYY-MM-DD'. */
  streakStartDate: string | null
  /** Auto-consumed to bridge a single missed non-Shabbat day. */
  freezesAvailable: number
}

export type StreakStatus = 'new' | 'active' | 'at_risk' | 'broken'

export interface StreakUpdateResult {
  state: DailyStreakState
  didIncrement: boolean
  isNewRecord: boolean
  brokeStreak: boolean
  freezesConsumed: number
}

export const MAX_FREEZES = 2
/** Earn one freeze each time the streak reaches a multiple of this. */
export const FREEZE_EARN_EVERY = 7

export function emptyStreakState(): DailyStreakState {
  return {
    currentStreak: 0,
    longestStreak: 0,
    lastActiveDate: null,
    streakStartDate: null,
    freezesAvailable: 0,
  }
}

// ---- pure date helpers (operate on LOCAL calendar days) ----

/** Local 'YYYY-MM-DD' for a Date (uses local time, not UTC). */
export function toDateKey(date: Date): string {
  const y = date.getFullYear()
  const m = `${date.getMonth() + 1}`.padStart(2, '0')
  const d = `${date.getDate()}`.padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Parse 'YYYY-MM-DD' to a local-midnight Date. */
export function parseDateKey(key: string): Date {
  const [y, m, d] = key.split('-').map((n) => parseInt(n, 10))
  return new Date(y, m - 1, d)
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date)
  d.setDate(d.getDate() + days)
  return d
}

function isSaturday(date: Date): boolean {
  return date.getDay() === 6 // 0=Sun ... 6=Sat
}

/** Whole local days from a -> b (b - a). Negative if b is before a. */
export function dayDiff(aKey: string, bKey: string): number {
  const a = parseDateKey(aKey).getTime()
  const b = parseDateKey(bKey).getTime()
  return Math.round((b - a) / 86_400_000)
}

/**
 * Number of NON-Saturday calendar days strictly between fromKey and toKey
 * (both exclusive). This is the "real" gap a streak must bridge — Saturdays
 * are transparent. e.g. Fri -> Sun has gap 0 (only Saturday between); Thu ->
 * Sun has gap 1 (Friday is a real missed day; Saturday is free).
 */
export function nonRestGapDays(fromKey: string, toKey: string): number {
  if (dayDiff(fromKey, toKey) <= 1) return 0
  let gap = 0
  let cursor = addDays(parseDateKey(fromKey), 1)
  const end = parseDateKey(toKey)
  while (cursor < end) {
    if (!isSaturday(cursor)) gap += 1
    cursor = addDays(cursor, 1)
  }
  return gap
}

/**
 * Apply one day's translation activity. Idempotent within the same local
 * day. `now` is injectable so this is fully testable.
 */
export function applyTranslation(state: DailyStreakState, now: Date = new Date()): StreakUpdateResult {
  const todayKey = toDateKey(now)
  const prev = { ...state }
  const noop: StreakUpdateResult = {
    state: prev,
    didIncrement: false,
    isNewRecord: false,
    brokeStreak: false,
    freezesConsumed: 0,
  }

  // Already counted today (or a clock that moved backwards) — no change.
  if (state.lastActiveDate && dayDiff(state.lastActiveDate, todayKey) <= 0) {
    return noop
  }

  let { currentStreak, longestStreak, streakStartDate, freezesAvailable } = state
  let brokeStreak = false
  let freezesConsumed = 0

  if (!state.lastActiveDate || currentStreak === 0) {
    // Fresh start.
    currentStreak = 1
    streakStartDate = todayKey
  } else {
    const gap = nonRestGapDays(state.lastActiveDate, todayKey)
    if (gap === 0) {
      currentStreak += 1 // consecutive (Saturday-bridged) day
    } else if (freezesAvailable >= gap) {
      freezesConsumed = gap
      freezesAvailable -= gap
      currentStreak += 1 // freeze(s) covered the missed non-Shabbat day(s)
    } else {
      brokeStreak = true // unbridgeable gap — restart at today
      currentStreak = 1
      streakStartDate = todayKey
    }
  }

  // Earn a freeze each time we land on a multiple of FREEZE_EARN_EVERY.
  if (!brokeStreak && currentStreak > 1 && currentStreak % FREEZE_EARN_EVERY === 0) {
    freezesAvailable = Math.min(MAX_FREEZES, freezesAvailable + 1)
  }

  const isNewRecord = currentStreak > longestStreak && currentStreak > 1
  longestStreak = Math.max(longestStreak, currentStreak)

  return {
    state: {
      currentStreak,
      longestStreak,
      lastActiveDate: todayKey,
      streakStartDate,
      freezesAvailable,
    },
    didIncrement: true,
    isNewRecord,
    brokeStreak,
    freezesConsumed,
  }
}

/**
 * Status for display, WITHOUT mutating state. `at_risk` = streak is still
 * alive but today's translation hasn't happened yet (this drives the
 * loss-aversion nudge). `broken` = a translation today would start over.
 */
export function computeStatus(state: DailyStreakState, now: Date = new Date()): StreakStatus {
  if (!state.lastActiveDate || state.currentStreak === 0) return 'new'
  const todayKey = toDateKey(now)
  if (state.lastActiveDate === todayKey) return 'active'
  if (dayDiff(state.lastActiveDate, todayKey) < 0) return 'active' // future/clock skew, be lenient
  const gap = nonRestGapDays(state.lastActiveDate, todayKey)
  return gap === 0 || state.freezesAvailable >= gap ? 'at_risk' : 'broken'
}

/** True once the at-risk streak has actually lapsed (for "you lost your streak"). */
export function isStreakBroken(state: DailyStreakState, now: Date = new Date()): boolean {
  return computeStatus(state, now) === 'broken'
}
